import React, { useMemo, useState } from 'react';
import { graphql } from 'gatsby';
import { getSrc } from '../../utils/media';
import GridSectionDS, {
    GridSectionProps,
} from 'design-system/src/components/organisms/GridSection';
import { Press } from '../../models/press';
import { useTranslate } from 'gatsby-theme-soluble-source';

interface PressSectionProps {
    title: string;
    titleTag: GridSectionProps['titleTag'];
    items: Press[];
}

function formatItems(items: Press[], ctaLabel: string) {
    if (!items.length) {
        return [];
    }

    return items.map((item) => ({
        ...item,
        ...(!!item.externalLink && {
            cta: {
                href: item.externalLink,
                label: ctaLabel,
                target: '_blank',
                ...(item.nofollow && { rel: 'noopener nofollow' }),
            },
        }),
        logo: {
            alt: item?.logo?.alt,
            src: getSrc(item.logo),
        },
    }));
}

export default function PressSection({ items, ...props }: PressSectionProps) {
    const [currentPage, setCurrentPage] = useState<number>(1);

    const _ = useTranslate();

    const formattedItems = useMemo(
        () => formatItems(items, _['press.cta.label'].text),
        [items, _],
    );

    return (
        <GridSectionDS
            {...props}
            items={formattedItems as any[]}
            variant={'grid-content-1'}
            viewMore={{
                label: _['cta.label.viewmore'].text,
                onClick: () => {
                    setCurrentPage((prevCurrentPage) => prevCurrentPage + 1);
                },
            }}
            pagination={{
                initialItems: 6,
                itemsPerPage: 6,
                page: currentPage,
            }}
        />
    );
}

export const query = graphql`
    fragment DatoCmsPressSection on DatoCmsPressSection {
        title
        titleTag
        items {
            logo {
                ...SourceMedia
                gatsbyImageData(placeholder: BLURRED, height: 72)
            }
            title
            text
            tag: dateFormatted
            externalLink
            nofollow
        }
    }
`;
